import { useState, useEffect, useCallback } from 'react';
import {
  createTransfert,
  saveTransfert,
  getTransfert,
  listTransferts,
  getItemsInTransfert,
  addItemToTransfert,
  deleteItemFromTransfert,
  validateTransfert,
  unvalidateTransfert,
} from '../models/transfertAPI';

export const useFetchAllTransferts = (params) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listTransferts(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return { items, total, loading, error, refetch: fetchData };
};

export const useFetchTransfertById = (id) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchOne = useCallback(async () => {
    if (!id) return;
    try { setLoading(true); const res = await getTransfert(id); setData(res.data); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchOne(); }, [fetchOne]);
  return { data, loading, error, refetch: fetchOne };
};

export const useFetchItemsInTransfert = (id) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchItems = useCallback(async () => {
    if (!id) return;
    try { setLoading(true); const res = await getItemsInTransfert(id); setItems(res.data || []); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  return { items, loading, error, refetch: fetchItems };
};

export const useCreateTransfert = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const create = async (payload) => {
    try { setLoading(true); const res = await createTransfert(payload); return res.data; }
    catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  };
  return { create, loading, error };
};

export const useSaveTransfert = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const save = async (transfert, items) => {
    try { setLoading(true); const res = await saveTransfert({ transfert, items }); return res.data; }
    catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  };
  return { save, loading, error };
};

export const useAddItemToTransfert = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const add = async (ref, item) => {
    try { setLoading(true); await addItemToTransfert(ref, item); }
    catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  };
  return { add, loading, error };
};

export const useDeleteItemFromTransfert = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const remove = async (ref, codeArt) => {
    try { setLoading(true); await deleteItemFromTransfert(ref, codeArt); }
    catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  };
  return { remove, loading, error };
};

export const useValidateTransfert = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isValidated, setIsValidated] = useState(false);

  const validate = async (id) => {
    try {
      setLoading(true);
      setError('');
      await validateTransfert(id);
      setIsValidated(true);
    } catch (e) {
      setError(e.message);
      setIsValidated(false);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { validate, loading, error, isValidated };
};

export const useUnvalidateTransfert = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const unvalidate = async (id) => {
    try {
      setLoading(true);
      setError('');
      await unvalidateTransfert(id);
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { unvalidate, loading, error };
};

